import { useAccountSettingByKey } from "@/entities/settings/hooks";
import {
  BooleanAccountSettingKey,
  isUnsetAccountSettingError,
  resolveBooleanAccountSetting
} from "@/entities/settings/accountSettingDefaults";

interface BooleanAccountSetting {
  // `undefined` until the stored value or the default is known, including after
  // a failed read. Gate on `value === true` so nothing the setting controls
  // shows up and then disappears, or shows up because the read failed.
  value: boolean | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * A boolean account setting. A stored value wins. The backend default applies
 * only when the settings API confirms the key is unset by answering 404. A
 * read still in flight, or one that failed for any other reason, has no value.
 */
export const useBooleanAccountSetting = (
  key: BooleanAccountSettingKey
): BooleanAccountSetting => {
  const { data, isError, error } = useAccountSettingByKey(key);
  if (data !== undefined) {
    return {
      value: resolveBooleanAccountSetting(key, data.value),
      isLoading: false,
      isError: false
    };
  }
  if (isError && isUnsetAccountSettingError(error)) {
    return {
      value: resolveBooleanAccountSetting(key, undefined),
      isLoading: false,
      isError: false
    };
  }
  return { value: undefined, isLoading: !isError, isError };
};
